/**
 * Shared UX copy + layout helpers for guided lessons where legacy drag/match/column
 * activities render as tap rounds (Duolingo-style), without worksheet mechanic wording.
 */

import { normalizeHeading, headingSimilarity } from './guidedPromptUtils';

/** Terms that should not appear in the main tap-round card (shell/engine). */
export const WORKSHEET_MECHANIC_PATTERN =
  /\b(drag|drop|dropping|columns?|clue\s+columns|boxes|chips|pieces|worksheet|match\s+this\s+clue|match\s+each\s+clue)\b/i;

/** @param {unknown} s */
export function stringHasWorksheetMechanicWording(s) {
  return typeof s === 'string' && WORKSHEET_MECHANIC_PATTERN.test(s);
}

/**
 * @param {unknown} raw
 * @returns {{ primary: string, secondary?: string }}
 */
export function splitKidFriendlyChoiceLabel(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return { primary: '' };
  const m = s.match(/^(.+?)\s*\(\s*([^)]+)\s*\)\s*$/);
  if (!m) return { primary: s };
  let primary = m[1].trim();
  let secondary = m[2].trim().toLowerCase();
  const pLow = primary.toLowerCase();
  if ((pLow === 'both' || pLow.startsWith('both ')) && /omni/.test(secondary)) {
    return { primary: 'Plants and meat', secondary: 'omnivore' };
  }
  return { primary, secondary };
}

/** Lowercase, trim, collapse space, strip punctuation for dedupe. */
export function normalizePromptForDedupe(s) {
  if (typeof s !== 'string') return '';
  return normalizeHeading(s)
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Ordered candidates: keep first; drop later if same normalized or highly similar to a kept line.
 * @param {{ text: string, kind?: string }[]} candidates
 * @returns {{ lines: string[], primary: string, suppressed: string[] }}
 */
export function pickPrimaryVisiblePrompt(candidates) {
  const kept = [];
  const suppressed = [];
  const SIM = 0.62;
  for (const c of candidates || []) {
    const t = typeof c?.text === 'string' ? c.text.trim() : '';
    if (!t) continue;
    const n = normalizePromptForDedupe(t);
    if (!n) continue;
    let drop = false;
    for (const prev of kept) {
      const pn = normalizePromptForDedupe(prev);
      if (n === pn || headingSimilarity(t, prev) >= SIM) {
        drop = true;
        suppressed.push(t);
        break;
      }
    }
    if (!drop) kept.push(t);
  }
  return { lines: kept, primary: kept[0] || '', suppressed };
}

/** True when the clue reads like a body/animal trait (safe for “This animal has …”). */
export function looksLikeAnimalTraitClue(clueText) {
  const t = typeof clueText === 'string' ? clueText.trim() : '';
  if (!t) return false;
  const low = t.toLowerCase();
  // Gear mechanics: "teeth" means gear teeth here, not an animal trait
  if (/\b(gear|gears|rpm|interlock|interlocks|reduction|overdrive|driven|driver|torque|mesh|meshed)\b/.test(low)) return false;
  const sceneCue =
    /\b(waves?|tide|barnacle|shore|spray|current|branch|branches|river|rainforest|forest|desert|ice|snow|mud|sand|reef|ocean|grass|meadow|pool|stream|creek)\b/.test(
      low,
    );
  const traitCue =
    /\b(molar|molars|teeth|tooth|tusk|fang|beak|bill|claw|claws|talon|fur|feathers?|scales?|hump|humps|webbed|whiskers|antlers?|trunk|giraffe|stripes?|spots?|camouflage|warm[- ]blooded)\b/.test(
      low,
    );
  if (traitCue && !sceneCue) return true;
  if (traitCue && sceneCue) return /\b(flat|sharp|curved|long|short|strong)\b/.test(low);
  return false;
}

/**
 * Habitat “scene” clues describe motion/sound in an environment (vs. a bare animal name).
 * @param {unknown} clueText
 */
export function looksLikeHabitatSceneSentence(clueText) {
  const t = typeof clueText === 'string' ? clueText.trim().toLowerCase() : '';
  if (!t) return false;
  return /\b(splash|splashes|crash|calls?|caw|dart|darts|swim|swims|flow|flows|rush|pour|drip|drift|flutter|hunt|hunts|gather|nest|roost|chirp|chirps)\b/.test(
    t,
  );
}

/** Lowercase phrase with a leading definite article when missing. */
function withDefiniteArticleLower(phrase) {
  const raw = typeof phrase === 'string' ? phrase.trim() : '';
  if (!raw) return 'this';
  const low = raw.toLowerCase();
  if (/^(the|a|an)\b/.test(low)) return low;
  return `the ${low}`;
}

function polishHabitatSceneBody(body) {
  let b = typeof body === 'string' ? body : '';
  b = b.replace(/\bin\s+cool\s+current\b/gi, 'in a cool current');
  return b;
}

/**
 * @param {string} blob
 * @returns {'diet'|'habitat'|'pattern'|'sequence'|'group'}
 */
export function inferClassificationDomainFromText(blob) {
  const b = (blob || '').toLowerCase();
  // Gear mechanics context — prevents "teeth" from triggering diet domain
  if (/\b(gear|gears|rpm|interlock|reduction|overdrive|driven|driver|torque|mesh|meshed)\b/.test(b)) return 'group';
  if (
    /\b(herbivore|carnivore|omnivore|diet|plants only|meat only|plants and meat|what does it eat|menu|molar|teeth|beak|food|snack|meal|eat|eating)\b/.test(
      b,
    )
  ) {
    return 'diet';
  }
  if (/\b(habitat|habitats|forest|ocean|desert|river|reef|live|lives|home|shelter|where does it live)\b/.test(b)) {
    return 'habitat';
  }
  if (/\b(pattern|repeat|next in the pattern|sequence of numbers)\b/.test(b)) {
    return 'pattern';
  }
  if (/\b(sequence|robot|steps|order|first\s+next|what should happen)\b/.test(b)) {
    return 'sequence';
  }
  return 'group';
}

/**
 * @param {'diet'|'habitat'|'pattern'|'sequence'|'group'} domain
 */
export function naturalMainQuestionForDomain(domain) {
  switch (domain) {
    case 'diet':
      return 'What does it eat?';
    case 'habitat':
      return 'Where does it live?';
    case 'pattern':
      return 'What comes next?';
    case 'sequence':
      return 'What should happen next?';
    default:
      return 'Which group fits?';
  }
}

function sentenceCasePeriod(s) {
  const t = typeof s === 'string' ? s.trim() : '';
  if (!t) return '';
  if (/[.!?…]$/.test(t)) return t;
  return `${t}.`;
}

const PLURAL_SUBJECT_START = /^(waves|minnows|parrots|birds|fish|trees|leaves|roots|they|clouds|drops|seeds)\b/i;

/**
 * Turn a habitat scene clue into one integrated question, or null to use split layout.
 * @param {string} clue
 * @returns {string | null}
 */
function habitatClueToIntegratedQuestion(clue) {
  const c = typeof clue === 'string' ? clue.trim() : '';
  if (!c || c.length > 72) return null;
  const tail = /[?]$/.test(c) ? c.slice(0, -1).trim() : c;
  const polished = polishHabitatSceneBody(tail.charAt(0).toLowerCase() + tail.slice(1));
  const body = polished;
  if (PLURAL_SUBJECT_START.test(tail)) {
    return `Where do ${body}?`;
  }
  return `Where does ${body}?`;
}

/**
 * Natural tap-round copy: one primary question and optional supporting clue line.
 * @param {{ clueText?: string, domain?: string, itemLabelShownSeparately?: boolean }}
 * @returns {{ mode: 'integrated'|'split', primaryQuestion: string, supportingClue?: string }}
 */
export function buildNaturalTapRoundPrompt({
  clueText,
  domain = 'group',
  itemLabelShownSeparately = false,
}) {
  const clue = typeof clueText === 'string' ? clueText.trim() : '';
  const d =
    domain === 'diet' || domain === 'habitat' || domain === 'pattern' || domain === 'sequence'
      ? domain
      : 'group';

  if (d === 'habitat' && clue) {
    if (looksLikeAnimalTraitClue(clue)) {
      const sc = /^(this|a|an)\b/i.test(clue)
        ? sentenceCasePeriod(clue)
        : sentenceCasePeriod(`This animal has ${clue.charAt(0).toLowerCase()}${clue.slice(1)}`);
      return { mode: 'split', supportingClue: sc, primaryQuestion: 'Which habitat is this?' };
    }
    if (itemLabelShownSeparately) {
      return { mode: 'integrated', primaryQuestion: naturalMainQuestionForDomain('habitat') };
    }
    const words = clue.trim().split(/\s+/).filter(Boolean);
    if (words.length === 1 && clue.trim().length <= 24) {
      return {
        mode: 'integrated',
        primaryQuestion: `Where does ${withDefiniteArticleLower(words[0])} live?`,
      };
    }
    if (looksLikeHabitatSceneSentence(clue)) {
      const integrated = habitatClueToIntegratedQuestion(clue);
      const genericHabitat = naturalMainQuestionForDomain('habitat');
      if (integrated && normalizePromptForDedupe(integrated) !== normalizePromptForDedupe(genericHabitat)) {
        return { mode: 'integrated', primaryQuestion: integrated };
      }
      return {
        mode: 'split',
        supportingClue: sentenceCasePeriod(clue),
        primaryQuestion: 'Which habitat is this?',
      };
    }
    return {
      mode: 'integrated',
      primaryQuestion: `Where does ${withDefiniteArticleLower(clue)} live?`,
    };
  }

  if (d === 'diet' && clue) {
    if (looksLikeAnimalTraitClue(clue)) {
      const sc = /^(this|a|an)\b/i.test(clue)
        ? sentenceCasePeriod(clue)
        : sentenceCasePeriod(`This animal has ${clue.charAt(0).toLowerCase()}${clue.slice(1)}`);
      return { mode: 'split', supportingClue: sc, primaryQuestion: 'What does it eat?' };
    }
    if (itemLabelShownSeparately) {
      return { mode: 'integrated', primaryQuestion: naturalMainQuestionForDomain('diet') };
    }
    const dietWords = clue.trim().split(/\s+/).filter(Boolean);
    if (dietWords.length === 1 && clue.trim().length <= 24) {
      return {
        mode: 'integrated',
        primaryQuestion: `What does ${withDefiniteArticleLower(dietWords[0])} eat?`,
      };
    }
    return {
      mode: 'split',
      supportingClue: sentenceCasePeriod(clue),
      primaryQuestion: 'What does this animal probably eat?',
    };
  }

  if (d === 'group' && clue && itemLabelShownSeparately) {
    return { mode: 'integrated', primaryQuestion: 'Where does it belong?' };
  }

  if (clue) {
    return {
      mode: 'split',
      supportingClue: sentenceCasePeriod(clue),
      primaryQuestion: naturalMainQuestionForDomain(d),
    };
  }

  return { mode: 'integrated', primaryQuestion: naturalMainQuestionForDomain(d) };
}

/**
 * Legacy helper: one line of body copy before choices (prefers supporting clue in split mode).
 * @param {unknown} clueText
 * @param {'diet'|'habitat'|'pattern'|'sequence'|'group'} [domain]
 */
export function clueStoryPromptLine(clueText, domain = 'group') {
  const { supportingClue, primaryQuestion, mode } = buildNaturalTapRoundPrompt({
    clueText,
    domain,
  });
  if (mode === 'split' && supportingClue) return supportingClue;
  return primaryQuestion || '';
}

/**
 * @param {{ targets?: { id?: string, label?: string }[], zoneLabels?: string[], stepTitle?: string, activityTitle?: string, instructions?: string }}
 */
export function inferNaturalClassificationQuestion(ctx) {
  const parts = [];
  if (typeof ctx.stepTitle === 'string') parts.push(ctx.stepTitle);
  if (typeof ctx.activityTitle === 'string') parts.push(ctx.activityTitle);
  if (typeof ctx.instructions === 'string') parts.push(ctx.instructions);
  for (const t of ctx.targets || []) {
    if (t?.label) parts.push(String(t.label));
    if (t?.id) parts.push(String(t.id));
  }
  for (const z of ctx.zoneLabels || []) {
    if (z) parts.push(String(z));
  }
  const blob = parts.join(' · ');
  const domain = inferClassificationDomainFromText(blob);
  return { question: naturalMainQuestionForDomain(domain), domain };
}

/**
 * Headline for lesson shell (replaces worksheet-like step titles on tap-first match/canvas).
 * @param {{ activityType?: string, stepTitle?: string, activityTitle?: string, content?: Record<string, unknown> }}
 * @returns {string | null} replacement title, or null to keep original
 */
export function suggestedTapRoundStepHeadline({ activityType, stepTitle, activityTitle, content }) {
  const st = typeof stepTitle === 'string' ? stepTitle : '';
  const at = typeof activityTitle === 'string' ? activityTitle : '';
  const ins = typeof content?.instructions === 'string' ? content.instructions : '';
  const isTapRoundish =
    activityType === 'match' ||
    (activityType === 'canvas' && Array.isArray(content?.pieces) && content.pieces.length > 0);
  if (!isTapRoundish) return null;
  const looksWorksheet =
    stringHasWorksheetMechanicWording(`${st} ${at} ${ins}`) || /\bclue\s+columns\b/i.test(`${st} ${at}`);
  if (!looksWorksheet) return null;
  const targets = Array.isArray(content?.targets) ? content.targets : [];
  const zones = Array.isArray(content?.scene?.zones) ? content.scene.zones : [];
  const zoneLabels = zones.map((z) => (typeof z?.label === 'string' ? z.label : '')).filter(Boolean);
  const { question } = inferNaturalClassificationQuestion({
    targets,
    zoneLabels,
    stepTitle: st,
    activityTitle: at,
    instructions: ins,
  });
  return question;
}

/**
 * @param {{ text?: string, explanation?: string, target?: { id?: string, label?: string }, domain?: string }}
 */
export function buildMatchTapRoundCorrectFeedback({ chip, target, domain }) {
  const clue = typeof chip?.text === 'string' ? chip.text.trim() : '';
  const expl = typeof chip?.explanation === 'string' ? chip.explanation.trim() : '';
  if (expl) return `Yes! ${expl}`;
  const tid = String(target?.id ?? '').toLowerCase();
  const tlab = String(target?.label ?? '').toLowerCase();
  const bundle = `${tid} ${tlab}`;
  const clueLow = clue.toLowerCase();

  if (domain === 'diet') {
    if (/herb|plants/.test(bundle) && /molar|grind|grass|hay|leaf|plant|chew/.test(clueLow)) {
      return 'Yes! Flat molars help animals chew plants like grass and hay.';
    }
    if (/carn|meat/.test(bundle) && /tear|hook|sharp|hunt|rib|prey|meat/.test(clueLow)) {
      return 'Yes! Sharp tearing teeth often go with a meaty menu.';
    }
    if (/omni|both/.test(bundle) && /berry|crayfish|both|mix|same week|plants and|meat and/.test(clueLow)) {
      return 'Yes! Eating different kinds of food is a clue it eats plants and meat.';
    }
  }
  const { primary } = splitKidFriendlyChoiceLabel(target?.label);
  if (primary) return `Yes! That fits ${primary}.`;
  return 'Yes! Nice work.';
}

/**
 * @param {{ piece?: { label?: string }, zone?: { label?: string }, domain?: string }}
 */
export function buildCanvasTapRoundCorrectFeedback({ piece, zone, domain }) {
  const name = typeof piece?.label === 'string' ? piece.label.trim() : 'This animal';
  const zlab = typeof zone?.label === 'string' ? zone.label : '';
  const { primary } = splitKidFriendlyChoiceLabel(zlab);
  if (domain === 'diet' && primary) {
    return `Yes! ${name} fits with ${primary}.`;
  }
  if (primary) {
    return `Yes! ${name} fits with ${primary}.`;
  }
  return `Yes! ${name} is in a good spot.`;
}
